import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { pollJob } from "@/lib/pollJob";
import { useAuth } from "@/context/AuthContext";
import AddToFolderModal from "@/components/AddToFolderModal";
import { MagnifyingGlass, FunnelSimple, Plus, CheckCircle, Storefront, Warning, FolderSimplePlus, CaretUp, CaretDown, CaretLeft, CaretRight, DownloadSimple, X, ShieldCheck, Lightning, Trash, Crosshair, EnvelopeSimple, Eye, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_BADGE = {
  courtier: { label: "Courtier", cls: "text-blue-700 bg-blue-50 border-blue-200" },
  agent_general: { label: "Agent général", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  mandataire: { label: "Mandataire", cls: "text-purple-700 bg-purple-50 border-purple-200" },
  inconnu: { label: "Inconnu", cls: "text-slate-500 bg-slate-50 border-slate-200" },
};
const SOURCE_LABEL = { orias: "Source : ORIAS (officiel)", manuel: "Type corrigé manuellement", heuristique: "Type estimé (heuristique) — à confirmer via ORIAS" };
const BACKEND_KEYS = ["q", "region", "activite", "compagnie", "tag", "type", "comm_min", "comm_max", "ca_min", "ca_max", "enriched"];

export default function Sourcing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [options, setOptions] = useState({ regions: [], activites: [], compagnies: [], tags: [], types: [] });
  const [filters, setFilters] = useState({ q: "", region: "", activite: "", compagnie: "", tag: "", type: "", comm_min: "", comm_max: "", ca_min: "", ca_max: "", age_min: "", age_max: "", enriched: "" });
  const [hideRadies, setHideRadies] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [sort, setSort] = useState({ key: "transmission", dir: "desc" });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  // Marché Pappers (recherche unitaire)
  const [pappers, setPappers] = useState(null);
  const [pappersLoading, setPappersLoading] = useState(false);
  const [importing, setImporting] = useState("");
  // Import en masse
  const [showBulk, setShowBulk] = useState(false);
  const [bulk, setBulk] = useState({ departement: "", limit: 50 });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);
  const [usage, setUsage] = useState(null);
  const [oriasLoading, setOriasLoading] = useState(false);
  // Import gratuit (API gouv)
  const [showGouv, setShowGouv] = useState(false);
  const [gouv, setGouv] = useState({ departement: "", limit: 100 });
  const [gouvLoading, setGouvLoading] = useState(false);
  const [gouvJob, setGouvJob] = useState(null);
  // Analyse 360° gratuite (data.gouv + BODACC)
  const [show360, setShow360] = useState(false);
  const [s360, setS360] = useState({ departement: "", age_min: "", niveau: "", target_count: "", max: "" });
  const [count360, setCount360] = useState(null);
  const [loading360, setLoading360] = useState(false);
  const [job360, setJob360] = useState(null);
  // Enrichissement en masse
  const [showEnrich, setShowEnrich] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [enrichMax, setEnrichMax] = useState("");
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichJob, setEnrichJob] = useState(null);
  const [showPostal, setShowPostal] = useState(false);
  const [postalPreview, setPostalPreview] = useState(null);
  const [postalJob, setPostalJob] = useState(null);
  const [postalLoading, setPostalLoading] = useState(false);
  const [postal, setPostal] = useState({
    societe: "AI Assurances", adresse1: "128 rue la Boétie", cp: "75008", ville: "Paris",
    signature: "Paul-Alexis RACINE", mention: "Confidentiel", mode: "normal",
  });
  const [smart, setSmart] = useState({ courtiers_only: false, departement: "", age_min: "", niveau: "", target_count: "" });
  const [priorityCount, setPriorityCount] = useState(0);
  // --- Campagnes email de masse ---
  const [showEmail, setShowEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState("Projet de rapprochement entre confrères");
  const [emailBody, setEmailBody] = useState("");
  const [emailFrom, setEmailFrom] = useState("paul@ai-assurances.com");
  const [emailSenders, setEmailSenders] = useState(["paul@ai-assurances.com", "amine.idrissi@ai-assurances.com"]);
  const [emailBcc, setEmailBcc] = useState("");
  const [abEnabled, setAbEnabled] = useState(false);
  const [emailSubjectB, setEmailSubjectB] = useState("Projet de rapprochement entre confrères");
  const [emailBodyB, setEmailBodyB] = useState("");
  const [emailFromB, setEmailFromB] = useState("amine.idrissi@ai-assurances.com");
  const [splitMode, setSplitMode] = useState("percent");
  const [splitValue, setSplitValue] = useState(50);
  const [emailSim, setEmailSim] = useState(null);
  const [emailFindJob, setEmailFindJob] = useState(null);
  const [emailJob, setEmailJob] = useState(null);
  const [emailFinding, setEmailFinding] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreviewing, setEmailPreviewing] = useState(false);
  const loadPriorityCount = () => api.get("/dashboard/kpis").then((r) => setPriorityCount(r.data.priority_targets || 0)).catch(() => {});

  const refreshEstimate = async (sm) => {
    try {
      const { data } = await api.post("/companies/enrich/estimate", { scope: "non_enriched", departement: sm.departement || undefined });
      setEstimate(data);
      setEnrichMax(String(data.max_affordable && data.max_affordable < data.count ? data.max_affordable : data.count));
    } catch { toast.error("Erreur estimation"); }
  };

  const openEnrich = async () => {
    const sm = { courtiers_only: false, departement: "", age_min: "", niveau: "", target_count: "" };
    setSmart(sm); setShowEnrich(true); setEstimate(null); setEnrichJob(null); setEnrichMax("");
    loadUsage();
    refreshEstimate(sm);
  };

  const updateSmart = (patch) => {
    const sm = { ...smart, ...patch };
    setSmart(sm);
    if ("departement" in patch) refreshEstimate(sm);
  };

  const isSmart = () => smart.courtiers_only || smart.departement || smart.age_min || smart.niveau || smart.target_count;

  const runEnrich = async () => {
    setEnrichLoading(true); setEnrichJob({ status: "queued", step: "Démarrage…" });
    try {
      const niveaux = smart.niveau === "eleve" ? ["élevé"] : smart.niveau === "modere" ? ["élevé", "modéré"] : [];
      const { data } = isSmart()
        ? await api.post("/companies/enrich-smart", {
            courtier: smart.courtiers_only, departement: smart.departement || undefined,
            age_min: smart.age_min ? Number(smart.age_min) : undefined, niveaux,
            target_count: smart.target_count ? Number(smart.target_count) : undefined,
            max: Number(enrichMax) || 0 })
        : await api.post("/companies/enrich-bulk", { scope: "non_enriched", max: Number(enrichMax) || 0 });
      const job = await pollJob(data.job_id, setEnrichJob, { maxTries: 300 });
      if (job?.status === "done") {
        toast.success(`${job.enriched_count} enrichi(s)${job.priority_found != null ? ` · ${job.priority_found} cible(s) prioritaire(s)` : ""}`);
        load(); loadUsage();
      } else if (job?.status === "error") { toast.error(job.error || "Erreur"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur enrichissement"); }
    finally { setEnrichLoading(false); }
  };

  const openPostal = async () => {
    setShowPostal(true); setPostalJob(null); setPostalPreview(null);
    try {
      const { data } = await api.post("/outreach/postal/bulk/preview", { company_ids: selectedIds });
      setPostalPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur aperçu"); }
  };

  const runPostal = async () => {
    if (!window.confirm(`Confirmer l'envoi de ${postalPreview?.eligible || 0} courrier(s) papier via Merci Facteur ? Chaque lettre est facturée.`)) return;
    setPostalLoading(true); setPostalJob({ status: "queued", step: "Démarrage…" });
    try {
      const { data } = await api.post("/outreach/postal/bulk", {
        company_ids: selectedIds,
        exp: { societe: postal.societe, nom: postal.societe, adresse1: postal.adresse1, cp: postal.cp, ville: postal.ville },
        signature: postal.signature, mention: postal.mention, mode: postal.mode,
      });
      const job = await pollJob(data.job_id, setPostalJob, { maxTries: 800 });
      if (job?.status === "done") { toast.success(job.step); load(); }
      else if (job?.status === "error") toast.error(job.error || "Erreur envoi");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi postal"); }
    finally { setPostalLoading(false); }
  };

  const [postalPreviewing, setPostalPreviewing] = useState(false);
  const previewPostal = async () => {
    setPostalPreviewing(true);
    try {
      const res = await api.post("/outreach/postal/bulk/preview-letter",
        { company_ids: selectedIds, signature: postal.signature, mention: postal.mention },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur aperçu courrier");
    } finally { setPostalPreviewing(false); }
  };

  const [publiLoading, setPubliLoading] = useState(false);
  const previewPubliDocx = async () => {
    setPostalPreviewing(true);
    try {
      const res = await api.post("/outreach/postal/publipostage/preview-docx",
        { company_ids: selectedIds, mention: postal.mention },
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type }));
      const a = document.createElement("a");
      a.href = url; a.download = "apercu-publipostage.docx"; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Aperçu Word téléchargé.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur aperçu publipostage");
    } finally { setPostalPreviewing(false); }
  };

  const runPublipostage = async () => {
    setPubliLoading(true);
    try {
      const exp = { societe: postal.societe, nom: postal.societe, adresse1: postal.adresse1, cp: postal.cp, ville: postal.ville };
      const { data: sim } = await api.post("/outreach/postal/publipostage",
        { company_ids: selectedIds, exp, mention: postal.mention, mode: postal.mode, dry_run: true });
      if (!window.confirm(`Publipostage Merci Facteur : ${sim.eligible} courrier(s) éligible(s) (${sim.ignored} ignoré(s)).\nLancer l'envoi de masse RÉEL ? Chaque lettre est facturée.`)) {
        setPubliLoading(false); return;
      }
      const { data } = await api.post("/outreach/postal/publipostage",
        { company_ids: selectedIds, exp, mention: postal.mention, mode: postal.mode, dry_run: false });
      toast.success(`Publipostage lancé · ${data.eligible} courrier(s) · envoi #${JSON.stringify(data.envoi_id)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur publipostage");
    } finally { setPubliLoading(false); }
  };

  const openEmail = async () => {
    setShowEmail(true); setEmailJob(null); setEmailFindJob(null); setEmailSim(null);
    try {
      const { data } = await api.post("/outreach/email/campaign", { company_ids: selectedIds, dry_run: true });
      setEmailSim(data);
      if (data.senders) setEmailSenders(data.senders);
      if (data.default_from) setEmailFrom(data.default_from);
      if (data.default_body) { setEmailBody((prev) => prev || data.default_body); setEmailBodyB((prev) => prev || data.default_body); }
      if (data.default_subject) setEmailSubject((prev) => prev || data.default_subject);
      if (data.default_bcc) setEmailBcc((prev) => prev || data.default_bcc);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const abPayload = () => (abEnabled ? {
    ab_enabled: true, split_mode: splitMode, split_value: Number(splitValue),
    variant_a: { from_email: emailFrom, subject: emailSubject, body: emailBody },
    variant_b: { from_email: emailFromB, subject: emailSubjectB, body: emailBodyB },
  } : { from_email: emailFrom, subject: emailSubject, body: emailBody });

  const refreshEmailSim = async () => {
    try {
      const { data } = await api.post("/outreach/email/campaign", { company_ids: selectedIds, dry_run: true });
      setEmailSim(data);
    } catch { /* silencieux */ }
  };

  const findEmailsBulk = async () => {
    setEmailFinding(true); setEmailFindJob({ status: "queued", step: "Démarrage…" });
    try {
      const { data } = await api.post("/outreach/email/find-bulk", { company_ids: selectedIds });
      const job = await pollJob(data.job_id, setEmailFindJob, { maxTries: 1500, interval: 3000 });
      if (job?.status === "done") { toast.success(job.step); await refreshEmailSim(); load(); }
      else if (job?.status === "error") toast.error(job.error || "Erreur");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur recherche emails"); }
    finally { setEmailFinding(false); }
  };

  const previewEmailCampaign = async (variant = "A") => {
    setEmailPreviewing(true);
    try {
      const v = variant === "B"
        ? { subject: emailSubjectB, body: emailBodyB, from_email: emailFromB }
        : { subject: emailSubject, body: emailBody, from_email: emailFrom };
      const { data } = await api.post("/outreach/email/campaign/preview", { company_ids: selectedIds, ...v });
      const w = window.open("", "_blank");
      w.document.write(`<title>Aperçu email ${abEnabled ? variant : ""} — ${data.nom || ""}</title><div style="max-width:640px;margin:24px auto;padding:20px;border:1px solid #e2e8f0;border-radius:10px"><div style="font-size:12px;color:#64748b;margin-bottom:12px">De : ${data.from || ""} · À : ${data.to || "—"} · Objet : <b>${data.subject}</b></div>${data.html}</div>`);
      w.document.close();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur aperçu"); }
    finally { setEmailPreviewing(false); }
  };

  const runEmailCampaign = async () => {
    if (!emailSim?.sender_verified) {
      toast.error("Envoi bloqué : domaine d'expéditeur non vérifié dans Resend.");
      return;
    }
    if (!window.confirm(`Envoyer la campagne à ${emailSim?.eligible || 0} destinataire(s) ? Cette action envoie de vrais emails.`)) return;
    setEmailSending(true); setEmailJob({ status: "queued", step: "Démarrage…" });
    try {
      const { data } = await api.post("/outreach/email/campaign", { company_ids: selectedIds, bcc: emailBcc, dry_run: false, ...abPayload() });
      const job = await pollJob(data.job_id, setEmailJob, { maxTries: 2000, interval: 3000 });
      if (job?.status === "done") { toast.success(job.step); load(); }
      else if (job?.status === "error") toast.error(job.error || "Erreur envoi");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur campagne email"); }
    finally { setEmailSending(false); }
  };

  const loadUsage = () => api.get("/pappers/usage").then((r) => setUsage(r.data)).catch(() => {});

  const syncCredit = async () => {
    const cur = usage?.credit?.remaining;
    const remaining = window.prompt("Solde de crédits Pappers restants (visible sur votre dashboard Pappers) :", cur != null ? String(cur) : "");
    if (remaining === null || remaining.trim() === "") return;
    const total = window.prompt("Total mensuel de votre forfait Pappers (crédits/mois) :", usage?.credit?.total ? String(usage.credit.total) : "500");
    const threshold = window.prompt("Seuil d'alerte (email envoyé quand le solde passe sous ce nombre) :", usage?.credit?.alert_threshold != null ? String(usage.credit.alert_threshold) : "50");
    try {
      await api.post("/pappers/credit", {
        remaining: Number(remaining.replace(",", ".")),
        total: total ? Number(total.replace(",", ".")) : undefined,
        alert_threshold: threshold ? Number(threshold.replace(",", ".")) : undefined,
      });
      toast.success("Solde Pappers synchronisé");
      loadUsage();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de synchronisation"); }
  };

  const bulkDelete = async () => {
    const n = selectedIds.length;
    if (!window.confirm(`Supprimer définitivement ${n} cabinet(s) ? Les deals, intégrations, analyses et activités liés seront aussi supprimés. Cette action est irréversible.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/companies/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted} cabinet(s) supprimé(s)`);
      setSelectedIds([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la suppression"); }
    finally { setDeleting(false); }
  };

  const verifyOriasSelected = async () => {
    setOriasLoading(true);
    try {
      const { data } = await api.post("/companies/orias-verify-bulk", { ids: selectedIds });
      toast.success(`${data.found}/${data.checked} cabinet(s) confirmés au registre ORIAS`);
      if (data.error) toast.error(`ORIAS: ${data.error.description || data.error.code}`);
      setSelectedIds([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur ORIAS"); }
    finally { setOriasLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    BACKEND_KEYS.forEach((k) => { if (filters[k] !== "") params[k] = filters[k]; });
    if (hideRadies) params.exclude_radies = true;
    const { data } = await api.get("/companies", { params });
    setCompanies(data);
    setLoading(false);
  };

  const searchPappers = async (term) => {
    const q = (term ?? filters.q).trim();
    if (!q) { toast.info("Saisissez un nom ou un SIREN à rechercher."); return; }
    setPappersLoading(true); setPappers(null);
    try {
      const { data } = await api.get("/pappers/search", { params: { q } });
      setPappers(data.results || []);
      if (data.error) toast.error(data.error);
    } catch { toast.error("Erreur Pappers"); } finally { setPappersLoading(false); }
  };

  const importCompany = async (r) => {
    setImporting(r.siren);
    try {
      await api.post("/companies/import-pappers", { siren: r.siren });
      toast.success(`${r.nom} importé (données financières réelles)`);
      setPappers((prev) => prev.map((x) => (x.siren === r.siren ? { ...x, in_base: true } : x)));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import"); }
    finally { setImporting(""); }
  };

  const runBulkImport = async () => {
    if (!bulk.departement.trim()) { toast.error("Indiquez un département (ex. 75)"); return; }
    setBulkLoading(true); setBulkJob({ status: "queued", step: "Démarrage…" });
    try {
      const { data } = await api.post("/companies/import-pappers-bulk", { departement: bulk.departement.trim(), limit: Number(bulk.limit) || 50 });
      const job = await pollJob(data.job_id, setBulkJob, { maxTries: 120 });
      if (job?.status === "done") {
        toast.success(`${job.imported_count} cabinet(s) importé(s) · ${job.skipped} déjà en base · ${job.total_available} disponibles dans le département`);
        load(); loadUsage();
      } else if (job?.status === "error") { toast.error(job.error || "Erreur d'import"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import en masse"); }
    finally { setBulkLoading(false); }
  };

  const refreshCount360 = async (st) => {
    try { const { data } = await api.post("/companies/enrich-360/estimate", { scope: "non_360", departement: st.departement || undefined }); setCount360(data.count); } catch (err) { console.error("estimate 360 a échoué:", err); }
  };
  const open360 = () => { const st = { departement: "", age_min: "", niveau: "", target_count: "", max: "" }; setS360(st); setShow360(true); setJob360(null); setCount360(null); refreshCount360(st); };
  const update360 = (patch) => { const st = { ...s360, ...patch }; setS360(st); if ("departement" in patch) refreshCount360(st); };
  const run360 = async () => {
    setLoading360(true); setJob360({ status: "queued", step: "Démarrage…" });
    try {
      const niveaux = s360.niveau === "eleve" ? ["élevé"] : s360.niveau === "modere" ? ["élevé", "modéré"] : [];
      const { data } = await api.post("/companies/enrich-360-bulk", {
        scope: "non_360", departement: s360.departement || undefined,
        age_min: s360.age_min ? Number(s360.age_min) : undefined, niveaux,
        target_count: s360.target_count ? Number(s360.target_count) : undefined,
        max: s360.max ? Number(s360.max) : undefined });
      const job = await pollJob(data.job_id, setJob360);
      if (job?.status === "done") { toast.success(`${job.enriched_count} cabinet(s) analysé(s) · ${job.priority_found} cible(s) prioritaire(s)`); load(); loadPriorityCount(); }
      else if (job?.status === "error") { toast.error(job.error || "Erreur analyse 360°"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur analyse 360°"); }
    finally { setLoading360(false); }
  };

  const runGouvImport = async () => {
    if (!gouv.departement.trim()) { toast.error("Indiquez un département (ex. 33)"); return; }
    setGouvLoading(true); setGouvJob({ status: "queued", step: "Démarrage…" });
    try {
      const { data } = await api.post("/companies/import-gouv-bulk", { departement: gouv.departement.trim(), limit: Number(gouv.limit) || 100 });
      const job = await pollJob(data.job_id, setGouvJob, { maxTries: 200 });
      if (job?.status === "done") { toast.success(`${job.imported_count} cabinet(s) importé(s) gratuitement · ${job.skipped} déjà en base`); load(); }
      else if (job?.status === "error") { toast.error(job.error || "Erreur d'import"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import gratuit"); }
    finally { setGouvLoading(false); }
  };

  useEffect(() => { api.get("/companies/filters/options").then((r) => setOptions(r.data)); }, []);
  useEffect(() => { loadPriorityCount(); const t = searchParams.get("tag"); if (t) setFilters((f) => ({ ...f, tag: t })); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (showBulk) { loadUsage(); setBulkJob(null); } }, [showBulk]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [filters, hideRadies]);

  const reset = () => { setFilters({ q: "", region: "", activite: "", compagnie: "", tag: "", type: "", comm_min: "", comm_max: "", ca_min: "", ca_max: "", age_min: "", age_max: "", enriched: "" }); setPappers(null); setSelectedIds([]); };
  const fmtk = (n) => (n == null ? "—" : Math.round(n / 1000) + " k€");

  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const getVal = (c, key) => ({
    nom: (c.nom || "").toLowerCase(), commissions: c.commissions || 0, ca: c.ca || 0,
    age: c.age_dirigeant ?? -1, transmission: c.scores?.transmission ?? 0,
    acquisition: c.scores?.acquisition ?? 0, risk: c.scores?.risk ?? 0,
  }[key]);

  const displayed = useMemo(() => {
    const amin = filters.age_min !== "" ? Number(filters.age_min) : null;
    const amax = filters.age_max !== "" ? Number(filters.age_max) : null;
    let rows = companies.filter((c) => {
      if (amin !== null && (c.age_dirigeant == null || c.age_dirigeant < amin)) return false;
      if (amax !== null && (c.age_dirigeant == null || c.age_dirigeant > amax)) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      const va = getVal(a, sort.key), vb = getVal(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [companies, filters.age_min, filters.age_max, sort]);

  const pageCount = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [companies, filters.age_min, filters.age_max, sort, hideRadies]);
  const clampedPage = Math.min(page, pageCount);
  const paged = useMemo(
    () => displayed.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE),
    [displayed, clampedPage]
  );

  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = displayed.length > 0 && selectedIds.length === displayed.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : displayed.map((c) => c.id));

  const Th = ({ k, children, align = "left" }) => (
    <th className={`font-medium px-4 py-3 overline cursor-pointer select-none hover:text-foreground text-${align}`}
      onClick={() => sortBy(k)} data-testid={`sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : align === "center" ? "justify-center" : ""}`}>
        {children}
        {sort.key === k && (sort.dir === "desc" ? <CaretDown size={11} weight="bold" /> : <CaretUp size={11} weight="bold" />)}
      </span>
    </th>
  );

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl">Deal Sourcing</h1>
        <div className="flex items-center gap-2">
          <button data-testid="open-pappers-btn" onClick={() => searchPappers()}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-border text-foreground hover:bg-accent">
            <Storefront size={16} /> Chercher (Pappers)
          </button>
          <button data-testid="open-gouv-import-btn" onClick={() => { setShowGouv(true); setGouvJob(null); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <DownloadSimple size={16} weight="bold" /> Import gratuit (API gouv)
          </button>
          <button data-testid="open-enrich360-btn" onClick={open360}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-teal-300 text-teal-700 hover:bg-teal-50">
            <Crosshair size={16} weight="bold" /> Analyse 360° (gratuit)
          </button>
          <button data-testid="open-enrich-btn" onClick={openEnrich}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-border text-foreground hover:bg-accent">
            <Lightning size={16} className="text-amber-500" weight="fill" /> Enrichir en masse
          </button>
          <button data-testid="open-bulk-import-btn" onClick={() => setShowBulk(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800">
            <Plus size={16} /> Importer des cabinets
          </button>
        </div>
      </header>

      <div className="p-8">
        {/* Filters */}
        <div className="bg-white border border-border p-4 mb-6">
          <div className="flex items-center gap-2 mb-3 text-muted-foreground">
            <FunnelSimple size={16} /><span className="overline">Recherche & filtres</span>
            <label className="ml-auto flex items-center gap-1.5 text-xs cursor-pointer select-none normal-case tracking-normal" data-testid="hide-radies-toggle">
              <input type="checkbox" checked={hideRadies} onChange={(e) => setHideRadies(e.target.checked)} className="accent-red-600" />
              Masquer les cabinets radiés
            </label>
            <button onClick={reset} className="text-xs hover:text-foreground">Réinitialiser</button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button data-testid="quick-priority-filter" onClick={() => setFilters((f) => ({ ...f, tag: f.tag === "Cible prioritaire" ? "" : "Cible prioritaire" }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${filters.tag === "Cible prioritaire" ? "bg-teal-600 text-white border-teal-600" : "border-teal-300 text-teal-700 hover:bg-teal-50"}`}>
              <Crosshair size={14} weight="bold" /> Cibles prioritaires{priorityCount ? ` (${priorityCount})` : ""}
            </button>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2 relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-3 text-muted-foreground" />
              <input data-testid="search-input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && searchPappers()}
                placeholder="Nom, ville, SIREN…" className="w-full pl-9 pr-3 py-2.5 text-sm border border-border focus:border-primary outline-none" />
            </div>
            <select data-testid="filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Type (tous)</option>
              {(options.types || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select data-testid="filter-region" value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}
              className="px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Région</option>
              {options.regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="filter-activite" value={filters.activite} onChange={(e) => setFilters({ ...filters, activite: e.target.value })}
              className="px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Activité</option>
              {options.activites.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select data-testid="filter-tag" value={filters.tag} onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
              className="px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Typologie (tag)</option>
              {options.tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="filter-enriched" value={filters.enriched} onChange={(e) => setFilters({ ...filters, enriched: e.target.value })}
              className="px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Enrichissement (tous)</option>
              <option value="true">Enrichis</option>
              <option value="false">Non enrichis (CA=0)</option>
            </select>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
            <select data-testid="filter-compagnie" value={filters.compagnie} onChange={(e) => setFilters({ ...filters, compagnie: e.target.value })}
              className="px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Compagnie</option>
              {options.compagnies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex gap-2 items-center"><span className="text-[11px] text-muted-foreground w-12">Comm.</span>
              <input data-testid="filter-comm-min" type="number" value={filters.comm_min} onChange={(e) => setFilters({ ...filters, comm_min: e.target.value })} placeholder="min" className="w-full px-2 py-2.5 text-sm border border-border outline-none focus:border-primary" />
              <input data-testid="filter-comm-max" type="number" value={filters.comm_max} onChange={(e) => setFilters({ ...filters, comm_max: e.target.value })} placeholder="max" className="w-full px-2 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            </div>
            <div className="flex gap-2 items-center lg:col-span-2"><span className="text-[11px] text-muted-foreground w-8">CA</span>
              <input data-testid="filter-ca-min" type="number" value={filters.ca_min} onChange={(e) => setFilters({ ...filters, ca_min: e.target.value })} placeholder="CA min €" className="w-full px-2 py-2.5 text-sm border border-border outline-none focus:border-primary" />
              <input data-testid="filter-ca-max" type="number" value={filters.ca_max} onChange={(e) => setFilters({ ...filters, ca_max: e.target.value })} placeholder="CA max €" className="w-full px-2 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            </div>
            <div className="flex gap-2 items-center"><span className="text-[11px] text-muted-foreground w-12">Âge dir.</span>
              <input data-testid="filter-age-min" type="number" value={filters.age_min} onChange={(e) => setFilters({ ...filters, age_min: e.target.value })} placeholder="min" className="w-full px-2 py-2.5 text-sm border border-border outline-none focus:border-primary" />
              <input data-testid="filter-age-max" type="number" value={filters.age_max} onChange={(e) => setFilters({ ...filters, age_max: e.target.value })} placeholder="max" className="w-full px-2 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div data-testid="bulk-action-bar" className="mb-3 flex items-center gap-3 bg-accent border border-primary/30 px-4 py-2.5">
            <span className="text-sm font-semibold text-primary">{selectedIds.length} cabinet(s) sélectionné(s)</span>
            <button data-testid="bulk-add-to-folder-btn" onClick={() => setShowFolderModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800">
              <FolderSimplePlus size={15} /> Ajouter à un dossier
            </button>
            <button data-testid="bulk-orias-verify-btn" onClick={verifyOriasSelected} disabled={oriasLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-50">
              <ShieldCheck size={15} /> {oriasLoading ? "Vérification…" : "Vérifier ORIAS"}
            </button>
            <button data-testid="bulk-postal-btn" onClick={openPostal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-primary text-primary hover:bg-accent">
              <EnvelopeSimple size={15} /> Envoi postal
            </button>
            <button data-testid="bulk-email-btn" onClick={openEmail}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-primary text-primary hover:bg-accent">
              <PaperPlaneTilt size={15} /> Campagne email
            </button>
            {user?.role === "super_admin" && (
              <button data-testid="bulk-delete-btn" onClick={bulkDelete} disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
                <Trash size={15} /> {deleting ? "Suppression…" : "Supprimer"}
              </button>
            )}
            <button onClick={() => setSelectedIds([])} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Désélectionner</button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" data-testid="select-all-companies" checked={allSelected} onChange={toggleAll} className="accent-primary cursor-pointer" />
                </th>
                <Th k="nom">Cabinet</Th>
                <th className="text-left font-medium px-4 py-3 overline">Type</th>
                <th className="text-left font-medium px-4 py-3 overline">Localisation</th>
                <Th k="commissions" align="right">Commissions</Th>
                <Th k="ca" align="right">CA</Th>
                <Th k="age" align="center">Âge dir.</Th>
                <Th k="transmission" align="center">Cession</Th>
                <Th k="acquisition" align="center">Acquis.</Th>
                <Th k="risk" align="center">Risque</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
              {!loading && displayed.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center">
                  <div className="text-muted-foreground mb-3">Aucun cabinet en base pour cette recherche.</div>
                  <button data-testid="open-bulk-import-cta" onClick={() => setShowBulk(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800">
                    <Plus size={15} /> Importer des cabinets
                  </button>
                </td></tr>
              )}
              {paged.map((c) => {
                const badge = TYPE_BADGE[c.type_activite] || TYPE_BADGE.inconnu;
                return (
                <tr key={c.id} onClick={() => window.open(`/cabinet/${c.id}`, "_blank")} data-testid={`company-row-${c.id}`}
                  title="Ouvrir la fiche dans un nouvel onglet"
                  className={`border-b border-border hover:bg-secondary cursor-pointer ${selectedIds.includes(c.id) ? "bg-accent/60" : ""}`}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" data-testid={`select-company-${c.id}`} checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} className="accent-primary cursor-pointer" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{c.nom}</span>
                      {c.radie && (
                        <span data-testid="radie-badge" title={c.date_radiation ? `Radié le ${new Date(String(c.date_radiation).slice(0,10)).toLocaleDateString("fr-FR")}` : "Radié / cessé"}
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full shrink-0">
                          <Warning size={11} weight="fill" /> Radié
                        </span>
                      )}
                      {c.npai && (
                        <span data-testid="npai-badge" title={`Courrier retourné (NPAI)${c.npai_reason ? ` — ${c.npai_reason}` : ""}. Adresse à vérifier.`}
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0">
                          <EnvelopeSimple size={11} weight="fill" /> NPAI
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">SIREN {c.siren}</div>
                    {c.tags && c.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.tags.slice(0, 4).map((t) => <span key={t} data-testid="company-tag" className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{t}</span>)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span data-testid={`type-badge-${c.id}`} title={SOURCE_LABEL[c.type_source] || ""}
                      className={`inline-flex items-center text-[10px] font-semibold border px-1.5 py-0.5 rounded-full whitespace-nowrap ${badge.cls}`}>
                      {badge.label}{c.type_source === "heuristique" && <span className="ml-0.5 opacity-60">?</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.ville}<div className="text-xs">{c.region}</div></td>
                  <td className="px-4 py-3 text-right font-mono">{eur(c.commissions)}</td>
                  <td className="px-4 py-3 text-right font-mono">{eur(c.ca)}</td>
                  <td className="px-4 py-3 text-center font-mono">{c.age_dirigeant ?? "—"}</td>
                  <td className="px-4 py-3 text-center font-mono font-semibold">{c.scores.transmission}%</td>
                  <td className="px-4 py-3 text-center font-mono font-semibold">{c.scores.acquisition}</td>
                  <td className="px-4 py-3 text-center font-mono font-semibold" style={{ color: c.scores.risk > 60 ? "#DC2626" : c.scores.risk > 40 ? "#D97706" : "#059669" }}>{c.scores.risk}</td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && displayed.length > 0 && (
          <div className="mt-3 flex items-center justify-between gap-4 flex-wrap" data-testid="sourcing-pagination">
            <div className="text-xs text-muted-foreground font-mono" data-testid="pagination-range">
              {(clampedPage - 1) * PAGE_SIZE + 1}–{Math.min(clampedPage * PAGE_SIZE, displayed.length)} sur {displayed.length}
              {companies.length >= 5000 && <span className="text-amber-600"> · liste plafonnée à 5000, affinez les filtres</span>}
            </div>
            {pageCount > 1 && (
              <div className="flex items-center gap-1">
                <button data-testid="page-prev" disabled={clampedPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed">
                  <CaretLeft size={13} weight="bold" /> Précédent
                </button>
                <span className="px-3 text-sm font-mono" data-testid="pagination-page">Page {clampedPage} / {pageCount}</span>
                <button data-testid="page-next" disabled={clampedPage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-border hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed">
                  Suivant <CaretRight size={13} weight="bold" />
                </button>
              </div>
            )}
          </div>
        )}
        {/* Marché Pappers (recherche unitaire) */}
        {(pappersLoading || pappers) && (
          <div className="mt-6 bg-white border border-border" data-testid="pappers-section">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Storefront size={16} className="text-primary" />
              <span className="font-head font-bold text-sm">Marché · Pappers</span>
              <span className="overline text-muted-foreground">données réelles</span>
              {pappers && <span className="ml-auto overline text-muted-foreground">{pappers.length} résultat(s)</span>}
            </div>
            <div className="divide-y divide-border">
              {pappersLoading && <div className="px-4 py-8 text-center text-muted-foreground text-sm">Recherche sur le marché…</div>}
              {pappers && pappers.length === 0 && !pappersLoading && (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">Aucune entreprise trouvée sur Pappers pour cette recherche.</div>
              )}
              {pappers && pappers.map((r, i) => (
                <div key={r.siren || i} className="flex items-center justify-between px-4 py-3" data-testid={`pappers-result-${i}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{r.nom}</div>
                    <div className="text-xs text-muted-foreground font-mono">SIREN {r.siren} · {r.ville || "—"} · CA {fmtk(r.ca)}</div>
                    {r.naf && <div className="text-[11px] text-muted-foreground truncate">{r.naf}</div>}
                  </div>
                  {r.in_base ? (
                    <span data-testid={`pappers-inbase-${i}`} className="flex items-center gap-1 text-xs text-[#059669] border border-[#a7f3d0] bg-[#ecfdf5] px-2 py-1 shrink-0">
                      <CheckCircle size={13} weight="fill" /> Déjà en base
                    </span>
                  ) : (
                    <button data-testid={`pappers-import-${i}`} onClick={() => importCompany(r)} disabled={importing === r.siren}
                      className="flex items-center gap-1 text-xs text-primary border border-primary px-2 py-1 hover:bg-accent shrink-0 disabled:opacity-50">
                      <Plus size={13} /> {importing === r.siren ? "Import…" : "Importer"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal enrichissement en masse */}
      {showEnrich && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="enrich-modal" onClick={() => !enrichLoading && setShowEnrich(false)}>
          <div className="bg-white w-full max-w-md border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2"><Lightning size={18} className="text-amber-500" weight="fill" /><span className="font-head font-bold">Enrichir en masse</span></div>
              <button onClick={() => { if (!enrichLoading) { setShowEnrich(false); setEnrichJob(null); } }} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground">Récupère les données financières réelles (CA, dirigeants, âge, ancienneté) des cabinets non enrichis via Pappers. Coût : <strong>~1 crédit par cabinet</strong>.</p>

              {!estimate ? (
                <div className="text-sm text-muted-foreground">Estimation en cours…</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-border p-3" data-testid="enrich-count"><div className="overline text-muted-foreground">Non enrichis</div><div className="font-mono text-2xl font-bold">{estimate.count}</div></div>
                    <div className="border border-border p-3"><div className="overline text-muted-foreground">Coût estimé</div><div className="font-mono text-2xl font-bold text-primary">≈ {estimate.est_cost}</div></div>
                  </div>
                  {estimate.remaining != null && (
                    <div className={`text-xs px-3 py-2 border ${estimate.sufficient ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="enrich-credit-note">
                      {estimate.sufficient
                        ? `Crédits suffisants : ${estimate.remaining} restants.`
                        : `Crédits insuffisants pour tout enrichir (${estimate.remaining} restants pour ${estimate.count} cabinets). L'enrichissement s'arrêtera automatiquement à court de crédits — limitez le nombre ci-dessous.`}
                    </div>
                  )}

                  {/* Ciblage intelligent */}
                  <div className="border border-indigo-200 bg-indigo-50/50 p-3 space-y-2.5" data-testid="smart-targeting">
                    <div className="flex items-center gap-1.5 text-[#4f46e5] font-semibold text-xs"><Lightning size={13} weight="fill" /> Ciblage intelligent (économise vos crédits)</div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 text-xs">
                        <input data-testid="smart-courtiers" type="checkbox" checked={smart.courtiers_only} onChange={(e) => setSmart({ ...smart, courtiers_only: e.target.checked })} />
                        Cibler les courtiers
                      </label>
                      <input data-testid="smart-dept" value={smart.departement} onChange={(e) => updateSmart({ departement: e.target.value })}
                        placeholder="Département (ex. 75)" className="px-2 py-1.5 text-xs border border-border outline-none focus:border-primary" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] uppercase text-muted-foreground">Âge dirigeant min (cible)</label>
                        <input data-testid="smart-age" type="number" value={smart.age_min} onChange={(e) => setSmart({ ...smart, age_min: e.target.value })}
                          placeholder="ex. 58" className="w-full mt-0.5 px-2 py-1.5 text-xs border border-border outline-none focus:border-primary" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase text-muted-foreground">Signal de transmission</label>
                        <select data-testid="smart-niveau" value={smart.niveau} onChange={(e) => setSmart({ ...smart, niveau: e.target.value })}
                          className="w-full mt-0.5 px-2 py-1.5 text-xs border border-border bg-white outline-none focus:border-primary">
                          <option value="">Peu importe</option>
                          <option value="modere">Modéré ou élevé</option>
                          <option value="eleve">Élevé uniquement</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-muted-foreground">Objectif : arrêt dès N cibles prioritaires trouvées (optionnel)</label>
                      <input data-testid="smart-target" type="number" value={smart.target_count} onChange={(e) => setSmart({ ...smart, target_count: e.target.value })}
                        placeholder="ex. 30 — laisse vide pour ne pas limiter" className="w-full mt-0.5 px-2 py-1.5 text-xs border border-border outline-none focus:border-primary" />
                    </div>
                    <p className="text-[10px] text-muted-foreground">Les cibles prioritaires (âge/signal) sont automatiquement taguées « Cible prioritaire ». L'enrichissement s'arrête dès l'objectif atteint pour économiser vos crédits.</p>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase text-muted-foreground">{isSmart() ? "Plafond d'enrichissements (sécurité)" : "Nombre à enrichir maintenant"}</label>
                    <input data-testid="enrich-max-input" type="number" min={1} max={estimate.count} value={enrichMax} onChange={(e) => setEnrichMax(e.target.value)}
                      className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
                  </div>
                </>
              )}

              {enrichJob && enrichLoading && (
                <div className="border border-primary/30 bg-accent px-3 py-2.5 text-sm" data-testid="enrich-progress">
                  <div className="font-medium text-primary">{enrichJob.step || "En cours…"}</div>
                  {enrichJob.total ? <div className="text-xs text-muted-foreground mt-1 font-mono">{enrichJob.enriched_count || 0}/{enrichJob.total} · {enrichJob.jetons_cost || 0} crédits{enrichJob.priority_found != null ? ` · ${enrichJob.priority_found} cible(s) prioritaire(s)` : ""}</div> : null}
                </div>
              )}
              {enrichJob && enrichJob.status === "done" && !enrichLoading && (
                <div className="border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="enrich-done">
                  ✓ {enrichJob.enriched_count} enrichi(s) · {enrichJob.jetons_cost} crédits{enrichJob.priority_found ? ` · ${enrichJob.priority_found} cible(s) prioritaire(s) taguée(s)` : ""}{enrichJob.stopped ? " · arrêt automatique" : ""}
                </div>
              )}

              <button data-testid="enrich-submit" onClick={runEnrich} disabled={enrichLoading || !estimate || !Number(enrichMax)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
                <Lightning size={16} /> {enrichLoading ? "Enrichissement en cours…" : `Enrichir ${Number(enrichMax) || 0} cabinet(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal envoi postal de masse */}
      {showPostal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="postal-bulk-modal">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-bold text-lg">Envoi postal de masse</h3>
              <button onClick={() => setShowPostal(false)} data-testid="postal-close"><X size={18} /></button>
            </div>
            {postalPreview ? (
              <div className="text-sm mb-4 bg-accent border border-primary/20 px-3 py-2" data-testid="postal-recap">
                <b>{postalPreview.eligible}</b> courrier(s) prêt(s) · <b>{postalPreview.ignored?.length || 0}</b> ignoré(s) (adresse incomplète) sur {postalPreview.selected} sélectionné(s).
                {postalPreview.ignored?.length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground max-h-24 overflow-y-auto">
                    {postalPreview.ignored.slice(0, 30).map((x) => <div key={x.id}>• {x.nom} — {x.raison}</div>)}
                  </div>
                )}
              </div>
            ) : <div className="text-sm text-muted-foreground mb-4">Calcul des destinataires éligibles…</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs block">Expéditeur (société)
                  <input value={postal.societe} onChange={(e) => setPostal({ ...postal, societe: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="postal-exp-societe" /></label>
                <label className="text-xs block">Signataire
                  <input value={postal.signature} onChange={(e) => setPostal({ ...postal, signature: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="postal-signature" /></label>
              </div>
              <label className="text-xs block">Adresse expéditeur
                <input value={postal.adresse1} onChange={(e) => setPostal({ ...postal, adresse1: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="postal-exp-adresse" /></label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs block">Code postal
                  <input value={postal.cp} onChange={(e) => setPostal({ ...postal, cp: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="postal-exp-cp" /></label>
                <label className="text-xs block">Ville
                  <input value={postal.ville} onChange={(e) => setPostal({ ...postal, ville: e.target.value })} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="postal-exp-ville" /></label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!postal.mention} onChange={(e) => setPostal({ ...postal, mention: e.target.checked ? "Confidentiel" : "" })} data-testid="postal-mention" />
                Mention « Confidentiel » sur le courrier
              </label>
              <div className="text-xs text-muted-foreground">Mode : Lettre verte (standard) · Modèle « premier contact » personnalisé par cabinet (ville + civilité). Chaque lettre est facturée par Merci Facteur.</div>
            </div>
            {postalJob && (
              <div className="mt-4 text-sm" data-testid="postal-progress">
                <div className="font-semibold">{postalJob.step || postalJob.status}</div>
                {postalJob.total > 0 && (
                  <div className="h-2 bg-secondary mt-2"><div className="h-2 bg-primary transition-all" style={{ width: `${Math.round(((postalJob.sent || 0) / postalJob.total) * 100)}%` }} /></div>
                )}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={previewPostal} disabled={postalPreviewing || !postalPreview?.eligible}
                className="px-4 py-2.5 text-sm font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-50 flex items-center gap-1.5" data-testid="postal-preview-btn">
                <Eye size={15} /> {postalPreviewing ? "Génération…" : "Prévisualiser"}
              </button>
              <button onClick={runPostal} disabled={postalLoading || !postalPreview?.eligible}
                className="flex-1 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50" data-testid="postal-send-btn">
                {postalLoading ? "Envoi en cours…" : `Envoyer ${postalPreview?.eligible || 0} courrier(s)`}
              </button>
              <button onClick={() => setShowPostal(false)} className="px-4 py-2.5 text-sm border border-border">Fermer</button>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">
                <strong>Gros volume (1000+)</strong> — Publipostage Merci Facteur : un seul envoi groupé, fusion côté La Poste.
                <span className="block mt-0.5 opacity-80">Note : le tampon « Confidentiel » de l'enveloppe personnalisée ne s'applique pas au publipostage (mention imprimée sur la lettre uniquement).</span>
              </p>
              <div className="flex gap-2">
                <button onClick={previewPubliDocx} disabled={postalPreviewing || !postalPreview?.eligible}
                  className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-accent disabled:opacity-50 flex items-center gap-1.5" data-testid="publi-preview-btn">
                  <DownloadSimple size={15} /> Aperçu Word
                </button>
                <button onClick={runPublipostage} disabled={publiLoading || !postalPreview?.eligible}
                  className="flex-1 py-2.5 text-sm font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="publipostage-btn">
                  <EnvelopeSimple size={15} /> {publiLoading ? "Publipostage en cours…" : `Publipostage (${postalPreview?.eligible || 0})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal campagne email de masse */}
      {showEmail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="email-campaign-modal">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-bold text-lg flex items-center gap-2"><PaperPlaneTilt size={20} className="text-primary" /> Campagne email de masse</h3>
              <button onClick={() => setShowEmail(false)} data-testid="email-close"><X size={18} /></button>
            </div>

            {emailSim ? (
              <div className="text-sm mb-4 bg-accent border border-primary/20 px-3 py-2" data-testid="email-recap">
                <b>{emailSim.eligible}</b> email(s) prêt(s) · <b>{emailSim.no_email}</b> sans email · <b>{emailSim.excluded}</b> exclu(s) (désinscrits/bounces).
              </div>
            ) : <div className="text-sm text-muted-foreground mb-4">Calcul des destinataires…</div>}

            {/* Étape 1 : recherche des emails en lot */}
            <div className="border border-border p-3 mb-3">
              <div className="text-xs font-semibold mb-1">1 · Trouver les emails manquants</div>
              <p className="text-xs text-muted-foreground mb-2">Recherche fiable via <b>Dropcontact</b> (RGPD, emails pros vérifiés à partir du dirigeant + SIREN), avec repli automatique sur le moteur interne. Consomme des crédits Dropcontact.</p>
              <button onClick={findEmailsBulk} disabled={emailFinding}
                className="w-full py-2 text-sm font-semibold border border-primary text-primary hover:bg-accent disabled:opacity-50" data-testid="email-find-btn">
                {emailFinding ? "Recherche en cours…" : `Trouver les emails (${selectedIds.length} cabinet(s))`}
              </button>
              {emailFindJob && (
                <div className="mt-2 text-xs" data-testid="email-find-progress">
                  <div className="font-semibold">{emailFindJob.step || emailFindJob.status}</div>
                  {emailFindJob.total > 0 && (
                    <div className="h-1.5 bg-secondary mt-1"><div className="h-1.5 bg-primary transition-all" style={{ width: `${Math.round(((emailFindJob.done_count || 0) / emailFindJob.total) * 100)}%` }} /></div>
                  )}
                </div>
              )}
            </div>

            {/* Étape 2 : contenu + envoi */}
            <div className="border border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold">2 · Contenu & envoi</div>
                <label className="text-xs flex items-center gap-1.5 cursor-pointer" data-testid="email-ab-toggle">
                  <input type="checkbox" checked={abEnabled} onChange={(e) => setAbEnabled(e.target.checked)} />
                  A/B testing
                </label>
              </div>

              <label className="text-xs block mb-2">Copie cachée (CCI)
                <input value={emailBcc} onChange={(e) => setEmailBcc(e.target.value)} placeholder="adresse(s) séparées par des virgules" className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="email-bcc" /></label>

              {abEnabled && (
                <div className="flex items-end gap-2 mb-3 bg-secondary/50 p-2 rounded">
                  <label className="text-xs flex-1">Répartition
                    <select value={splitMode} onChange={(e) => setSplitMode(e.target.value)} className="w-full border border-border px-2 py-1.5 text-sm mt-1 bg-white" data-testid="email-split-mode">
                      <option value="percent">En % (variante A)</option>
                      <option value="count">En nombre (variante A)</option>
                    </select></label>
                  <label className="text-xs w-28">{splitMode === "percent" ? "% pour A" : "Nb pour A"}
                    <input type="number" min="0" value={splitValue} onChange={(e) => setSplitValue(e.target.value)} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="email-split-value" /></label>
                  {emailSim && (() => {
                    const el = emailSim.eligible || 0;
                    const na = splitMode === "count" ? Math.min(Number(splitValue) || 0, el) : Math.round(el * (Number(splitValue) || 0) / 100);
                    return <div className="text-xs text-muted-foreground pb-2">A: <b>{na}</b> · B: <b>{el - na}</b></div>;
                  })()}
                </div>
              )}

              {/* Variante A */}
              <div className="mb-3">
                {abEnabled && <div className="text-xs font-bold text-primary mb-1">Variante A</div>}
                <label className="text-xs block mb-2">Expéditeur
                  <select value={emailFrom} onChange={(e) => setEmailFrom(e.target.value)} className="w-full border border-border px-2 py-1.5 text-sm mt-1 bg-white" data-testid="email-from">
                    {emailSenders.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select></label>
                <label className="text-xs block mb-2">Objet
                  <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="email-subject" /></label>
                <label className="text-xs block mb-1">Contenu
                  <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={abEnabled ? 8 : 12} className="w-full border border-border px-2 py-1.5 text-sm mt-1 font-mono leading-relaxed" data-testid="email-body" /></label>
                <button onClick={() => previewEmailCampaign("A")} disabled={emailPreviewing || !emailSim?.eligible}
                  className="text-xs font-semibold text-primary hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="email-preview-btn">
                  <Eye size={13} /> Aperçu {abEnabled ? "variante A" : ""}
                </button>
              </div>

              {/* Variante B */}
              {abEnabled && (
                <div className="mb-3 border-t border-border pt-3">
                  <div className="text-xs font-bold text-primary mb-1">Variante B</div>
                  <label className="text-xs block mb-2">Expéditeur
                    <select value={emailFromB} onChange={(e) => setEmailFromB(e.target.value)} className="w-full border border-border px-2 py-1.5 text-sm mt-1 bg-white" data-testid="email-from-b">
                      {emailSenders.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select></label>
                  <label className="text-xs block mb-2">Objet
                    <input value={emailSubjectB} onChange={(e) => setEmailSubjectB(e.target.value)} className="w-full border border-border px-2 py-1.5 text-sm mt-1" data-testid="email-subject-b" /></label>
                  <label className="text-xs block mb-1">Contenu
                    <textarea value={emailBodyB} onChange={(e) => setEmailBodyB(e.target.value)} rows={8} className="w-full border border-border px-2 py-1.5 text-sm mt-1 font-mono leading-relaxed" data-testid="email-body-b" /></label>
                  <button onClick={() => previewEmailCampaign("B")} disabled={emailPreviewing || !emailSim?.eligible}
                    className="text-xs font-semibold text-primary hover:underline flex items-center gap-1 disabled:opacity-50" data-testid="email-preview-btn-b">
                    <Eye size={13} /> Aperçu variante B
                  </button>
                </div>
              )}

              <div className="text-xs text-muted-foreground mb-2">Variables : <code className="bg-secondary px-1">{"{civilite_nom}"}</code> et <code className="bg-secondary px-1">{"{ville}"}</code>. Lien de désinscription discret ajouté automatiquement (RGPD).</div>

              {!emailSim?.sender_verified && (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 mb-2 rounded" data-testid="email-sender-warning">
                  ⚠️ <b>Envoi verrouillé</b> : le domaine d'expéditeur <b>{emailSim?.sender || "—"}</b> n'est pas vérifié dans Resend. La <b>prévisualisation</b> reste disponible.
                </div>
              )}

              {emailJob && (
                <div className="mb-2 text-sm" data-testid="email-progress">
                  <div className="font-semibold">{emailJob.step || emailJob.status}</div>
                  {emailJob.total > 0 && (
                    <div className="h-2 bg-secondary mt-2"><div className="h-2 bg-primary transition-all" style={{ width: `${Math.round(((emailJob.sent || 0) / emailJob.total) * 100)}%` }} /></div>
                  )}
                </div>
              )}

              <button onClick={runEmailCampaign} disabled={emailSending || !emailSim?.eligible || !emailSim?.sender_verified}
                className="w-full py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50" data-testid="email-send-btn">
                {emailSending ? "Envoi en cours…" : `Envoyer à ${emailSim?.eligible || 0} destinataire(s)${abEnabled ? " (A/B)" : ""}`}
              </button>
            </div>

            <button onClick={() => setShowEmail(false)} className="w-full mt-3 py-2 text-sm border border-border">Fermer</button>
          </div>
        </div>
      )}

      {/* Modal import en masse */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="bulk-import-modal" onClick={() => !bulkLoading && setShowBulk(false)}>
          <div className="bg-white w-full max-w-md border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2"><DownloadSimple size={18} className="text-primary" /><span className="font-head font-bold">Importer des cabinets</span></div>
              <button onClick={() => { if (!bulkLoading) { setShowBulk(false); setBulkJob(null); } }} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-muted-foreground">Import en masse des cabinets d'assurance (NAF 66.22Z) d'un <strong>département</strong> via Pappers. Le « Type » (courtier / agent) est estimé automatiquement.</p>

              {/* Crédits Pappers (solde de référence) */}
              {usage && (
                <div className="border border-border bg-slate-50 px-3 py-2.5 text-xs" data-testid="pappers-usage">
                  {usage.credit ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Crédits Pappers restants ≈</span>
                        <span className={`font-mono font-semibold text-sm ${usage.credit.low ? "text-red-600" : ""}`}>{usage.credit.remaining}{usage.credit.total ? ` / ${usage.credit.total}` : ""}</span>
                      </div>
                      {usage.credit.total ? (
                        <div className="mt-1.5 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className={`h-full ${usage.credit.low ? "bg-red-500" : "bg-primary"}`}
                            style={{ width: `${Math.max(0, Math.min(100, Math.round((usage.credit.remaining / usage.credit.total) * 100)))}%` }} />
                        </div>
                      ) : null}
                      {usage.credit.low && (
                        <div className="mt-2 flex items-start gap-1.5 text-red-700 bg-red-50 border border-red-200 px-2 py-1.5 rounded" data-testid="credit-low-alert">
                          <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                          <span>Crédits bas (seuil {usage.credit.alert_threshold}). Une alerte email a été envoyée. Pensez à recharger avant votre prochaine campagne.</span>
                        </div>
                      )}
                      <div className="text-[11px] text-muted-foreground mt-1">Consommé via l'app depuis la synchro : {usage.credit.consumed_since}</div>
                    </>
                  ) : (
                    <div className="text-muted-foreground">Solde Pappers non synchronisé. Renseignez votre solde restant pour suivre le « restant ≈ ».</div>
                  )}
                  <div className="flex items-center gap-3 mt-1.5">
                    <button data-testid="pappers-sync-btn" type="button" onClick={syncCredit} className="text-primary hover:underline font-medium">
                      Synchroniser le solde
                    </button>
                    <a href={usage.dashboard_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">Dashboard Pappers ↗</a>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Département</label>
                <input data-testid="bulk-dept-input" value={bulk.departement} onChange={(e) => setBulk({ ...bulk, departement: e.target.value })}
                  placeholder="ex. 75, 69, 13…" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Nombre max à importer (jusqu'à 200)</label>
                <input data-testid="bulk-limit-input" type="number" min={1} max={200} value={bulk.limit} onChange={(e) => setBulk({ ...bulk, limit: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
              </div>

              {/* Progression du job */}
              {bulkJob && bulkLoading && (
                <div className="border border-primary/30 bg-accent px-3 py-2.5 text-sm" data-testid="bulk-progress">
                  <div className="font-medium text-primary">{bulkJob.step || "En cours…"}</div>
                  {bulkJob.total_available ? (
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      {bulkJob.imported_count || 0} importés · {bulkJob.skipped || 0} déjà en base · {bulkJob.total_available} disponibles
                    </div>
                  ) : null}
                </div>
              )}
              {bulkJob && bulkJob.status === "done" && !bulkLoading && (
                <div className="border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="bulk-done">
                  ✓ {bulkJob.imported_count} cabinet(s) importé(s) · {bulkJob.skipped} déjà en base · {bulkJob.total_available} disponibles dans le département ({bulkJob.jetons_cost} jetons utilisés)
                </div>
              )}

              <button data-testid="bulk-import-submit" onClick={runBulkImport} disabled={bulkLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
                <DownloadSimple size={16} /> {bulkLoading ? "Import en cours…" : "Lancer l'import"}
              </button>
              <p className="text-[11px] text-muted-foreground">Astuce : pour un cabinet précis, utilise plutôt « Chercher (Pappers) » en haut puis « Importer ». Les cabinets déjà présents ne sont pas réimportés.</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal import gratuit (API gouv) */}
      {showGouv && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="gouv-import-modal" onClick={() => !gouvLoading && setShowGouv(false)}>
          <div className="bg-white w-full max-w-md border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2"><DownloadSimple size={18} className="text-emerald-600" weight="bold" /><span className="font-head font-bold">Import gratuit (API gouv)</span></div>
              <button onClick={() => { if (!gouvLoading) { setShowGouv(false); setGouvJob(null); } }} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
                <strong>100% gratuit</strong> · aucun crédit Pappers consommé. Import des cabinets d'assurance (NAF 66.22Z) <strong>dont le siège est dans le département</strong> via l'annuaire des entreprises (data.gouv). Âge des dirigeants et signal de transmission calculés automatiquement. Utilisez Pappers ensuite pour l'enrichissement financier.
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Département</label>
                <input data-testid="gouv-dept-input" value={gouv.departement} onChange={(e) => setGouv({ ...gouv, departement: e.target.value })}
                  placeholder="ex. 33, 75, 69…" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Nombre max à importer (jusqu'à 500)</label>
                <input data-testid="gouv-limit-input" type="number" min={1} max={500} value={gouv.limit} onChange={(e) => setGouv({ ...gouv, limit: e.target.value })}
                  className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-emerald-500" />
              </div>

              {gouvJob && gouvLoading && (
                <div className="border border-emerald-300/50 bg-emerald-50 px-3 py-2.5 text-sm" data-testid="gouv-progress">
                  <div className="font-medium text-emerald-700">{gouvJob.step || "En cours…"}</div>
                  {gouvJob.scanned ? (
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                      {gouvJob.imported_count || 0} importés · {gouvJob.scanned || 0} scannés · {gouvJob.skipped || 0} déjà en base
                    </div>
                  ) : null}
                </div>
              )}
              {gouvJob && gouvJob.status === "done" && !gouvLoading && (
                <div className="border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="gouv-done">
                  ✓ {gouvJob.imported_count} cabinet(s) importé(s) gratuitement · {gouvJob.skipped} déjà en base · {gouvJob.scanned} scannés
                </div>
              )}

              <button data-testid="gouv-import-submit" onClick={runGouvImport} disabled={gouvLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                <DownloadSimple size={16} /> {gouvLoading ? "Import en cours…" : "Lancer l'import gratuit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal analyse 360° gratuite */}
      {show360 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="enrich360-modal" onClick={() => !loading360 && setShow360(false)}>
          <div className="bg-white w-full max-w-md border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2"><Crosshair size={18} className="text-teal-600" weight="bold" /><span className="font-head font-bold">Analyse 360° (gratuit)</span></div>
              <button onClick={() => { if (!loading360) { setShow360(false); setJob360(null); } }} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="border border-teal-200 bg-teal-50 px-3 py-2.5 text-xs text-teal-800">
                <strong>100% gratuit</strong> · croise data.gouv (âges dirigeants, effectif) + <strong>BODACC</strong> (cessions, procédures) pour calculer le <strong>signal de transmission</strong> et faire remonter les meilleures cibles — <strong>sans aucun crédit Pappers</strong>. Les cibles correspondant aux critères (ou avec une cession/procédure en cours) sont taguées « Cible prioritaire ».
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Département (option.)</label>
                  <input data-testid="enrich360-dept" value={s360.departement} onChange={(e) => update360({ departement: e.target.value })}
                    placeholder="ex. 33" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Âge dirigeant min.</label>
                  <input data-testid="enrich360-age" type="number" min={0} value={s360.age_min} onChange={(e) => update360({ age_min: e.target.value })}
                    placeholder="ex. 58" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Signal min.</label>
                  <select data-testid="enrich360-niveau" value={s360.niveau} onChange={(e) => update360({ niveau: e.target.value })}
                    className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-teal-500 bg-white">
                    <option value="">Tous</option>
                    <option value="modere">Modéré ou élevé</option>
                    <option value="eleve">Élevé uniquement</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase text-muted-foreground">Arrêt à N cibles</label>
                  <input data-testid="enrich360-target" type="number" min={1} value={s360.target_count} onChange={(e) => update360({ target_count: e.target.value })}
                    placeholder="auto" className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-teal-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Nombre max à analyser</label>
                <input data-testid="enrich360-max" type="number" min={1} value={s360.max} onChange={(e) => update360({ max: e.target.value })}
                  placeholder={count360 != null ? `${count360} cabinet(s) jamais analysé(s)` : "tous"} className="w-full mt-1 px-3 py-2.5 text-sm border border-border outline-none focus:border-teal-500" />
                {count360 != null && <p className="text-[11px] text-muted-foreground mt-1" data-testid="enrich360-count">{count360} cabinet(s) jamais analysé(s) en 360°{s360.departement ? ` dans le ${s360.departement}` : ""}.</p>}
              </div>

              {job360 && loading360 && (
                <div className="border border-teal-300/50 bg-teal-50 px-3 py-2.5 text-sm" data-testid="enrich360-progress">
                  <div className="font-medium text-teal-700">{job360.step || "En cours…"}</div>
                  {job360.total ? (
                    <div className="text-xs text-muted-foreground mt-1 font-mono">{job360.enriched_count || 0}/{job360.total} analysés · {job360.priority_found || 0} cible(s) prioritaire(s)</div>
                  ) : null}
                </div>
              )}
              {job360 && job360.status === "done" && !loading360 && (
                <div className="border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="enrich360-done">
                  ✓ {job360.enriched_count} cabinet(s) analysé(s) gratuitement · {job360.priority_found} cible(s) prioritaire(s) taguée(s)
                </div>
              )}

              <button data-testid="enrich360-submit" onClick={run360} disabled={loading360}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
                <Crosshair size={16} /> {loading360 ? "Analyse en cours…" : "Lancer l'analyse 360° gratuite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFolderModal && (
        <AddToFolderModal companyIds={selectedIds} onClose={() => setShowFolderModal(false)} onDone={() => setSelectedIds([])} />
      )}
    </div>
  );
}
